import * as React from 'react';
import { FC, useMemo } from 'react';

import { Box } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import Currency from 'components/currency';
import { getFeesByGroup, getStandardizedName } from 'pages/cart/your-cart/totals/utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { computeOtherDiscountAmount } from 'utils/cart/helper';
import { getDeliveryFees } from 'utils/order';

import { geographicalFeeLabelIdMap } from '../../constants';

import getRewardsDiscount from './get-rewards-discount';
import {
  FeeDeliveryItem,
  ReceiptDetail,
  ReceiptDetailsText,
  RewardsDiscountDetail,
  SDiscount,
} from './receipt-details.styled';
import { IOrderFooterProps } from './types';

const ReceiptDetails: FC<React.PropsWithChildren<IOrderFooterProps>> = ({ order }) => {
  const { formatCurrencyForLocale: currencyFormatter } = useUIContext();
  const { delivery } = order;
  const { subTotalCents, taxCents, totalCents } = order.cart;
  const { formatMessage } = useIntl();
  const {
    feeCents: deliveryFeeCents,
    isFreeDelivery,
    deliveryFeeDiscountCents,
    geographicalFeeCents,
    serviceFeeCents,
    smallCartFeeCents,
  } = getDeliveryFees({
    delivery,
  });

  const enableHideTaxLine = useFlag(LaunchDarklyFlag.ENABLE_HIDE_TAX_LINE);
  const enableFeesOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_FEES_ON_ORDER_RECEIPT);
  const enableDiscountsOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_DISCOUNTS_ON_ORDER_RECEIPT);

  const discounts = order.cart.discounts || [];
  const dropoffState = delivery?.dropoff?.state;

  // Use a custom label for geographical delivery fees as required
  const geographicalFeeLabelId =
    dropoffState && geographicalFeeLabelIdMap.hasOwnProperty(dropoffState)
      ? geographicalFeeLabelIdMap[dropoffState]
      : 'geographicalFee';

  const rewardsDiscountCents = useMemo(
    () => getRewardsDiscount({ discounts, enableDiscountsOnOrderReceipt }),
    [discounts, enableDiscountsOnOrderReceipt]
  );

  const otherDiscountAmount = computeOtherDiscountAmount(discounts);

  const fees = order.cart.fees ?? [];
  const itemFees = getFeesByGroup(fees);

  return (
    <Box>
      {enableFeesOnOrderReceipt &&
        !!itemFees &&
        Object.entries(itemFees).map(([name, entryCents]: [string, number], index) => (
          <ReceiptDetail key={index}>
            <ReceiptDetailsText>
              {formatMessage({ id: `${getStandardizedName({ name })}Fee` })}
            </ReceiptDetailsText>

            <ReceiptDetailsText>{currencyFormatter(entryCents)}</ReceiptDetailsText>
          </ReceiptDetail>
        ))}
      {order.delivery && (
        <Box>
          <ReceiptDetail>
            <ReceiptDetailsText>{formatMessage({ id: 'deliveryFee' })}:</ReceiptDetailsText>
            {isFreeDelivery ? (
              <FeeDeliveryItem>
                {formatMessage({ id: 'free' })}
                &nbsp;
                <SDiscount>
                  <Currency amount={deliveryFeeDiscountCents} />
                </SDiscount>
              </FeeDeliveryItem>
            ) : (
              <ReceiptDetailsText>{currencyFormatter(deliveryFeeCents)}</ReceiptDetailsText>
            )}
          </ReceiptDetail>
          {geographicalFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>
                {formatMessage({ id: geographicalFeeLabelId })}:
              </ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(geographicalFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          {serviceFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{formatMessage({ id: 'serviceFee' })}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(serviceFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          {smallCartFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{formatMessage({ id: 'smallCartFee' })}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(smallCartFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          <ReceiptDetail>
            <ReceiptDetailsText>{formatMessage({ id: 'tip' })}:</ReceiptDetailsText>
            <ReceiptDetailsText>{currencyFormatter(order.delivery.tipCents)}</ReceiptDetailsText>
          </ReceiptDetail>
        </Box>
      )}
      {enableDiscountsOnOrderReceipt && !!otherDiscountAmount && (
        <RewardsDiscountDetail>
          <ReceiptDetailsText>{formatMessage({ id: 'otherDiscounts' })}</ReceiptDetailsText>
          <ReceiptDetailsText>{` - ${currencyFormatter(otherDiscountAmount)}`}</ReceiptDetailsText>
        </RewardsDiscountDetail>
      )}
      {!!rewardsDiscountCents && (
        <RewardsDiscountDetail>
          <ReceiptDetailsText color={primitive.bk.$crunchyGreen}>
            {formatMessage({ id: 'rewardSavings' })}
          </ReceiptDetailsText>
          <ReceiptDetailsText color={primitive.bk.$crunchyGreen}>
            {` - ${currencyFormatter(rewardsDiscountCents)}`}
          </ReceiptDetailsText>
        </RewardsDiscountDetail>
      )}

      <ReceiptDetail>
        <ReceiptDetailsText>{formatMessage({ id: 'subtotal' })}</ReceiptDetailsText>
        <ReceiptDetailsText>{currencyFormatter(subTotalCents)}</ReceiptDetailsText>
      </ReceiptDetail>
      {!enableHideTaxLine && (
        <ReceiptDetail>
          <ReceiptDetailsText>{formatMessage({ id: 'tax' })}</ReceiptDetailsText>
          <ReceiptDetailsText>{currencyFormatter(taxCents)}</ReceiptDetailsText>
        </ReceiptDetail>
      )}
      {/* TODO Add bag fee here when available from backend. */}
      <ReceiptDetail $total>
        <ReceiptDetailsText $total>{formatMessage({ id: 'total' })}</ReceiptDetailsText>
        <ReceiptDetailsText $total>{currencyFormatter(totalCents)}</ReceiptDetailsText>
      </ReceiptDetail>
    </Box>
  );
};

export default ReceiptDetails;
